import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import {
    type SpendProductDetailResponseDto,
    type SpendProductRowDto,
    buildSpendInsightsBody,
    getReportSpendLastRequestParams,
} from 'entities/ReportSpendSummary';
import { $api } from 'shared/api/api';
import { useSpendSummaryReportVisible } from 'shared/lib/hooks/useSpendSummaryReportVisible';
import { toNumber } from 'shared/lib/number/number';
import {
    type SpendDrillInLocationState,
    buildSpendDrillInRequestParams,
} from 'widgets/SpendSummaryReport/model/types/spendDrillIn';

interface UseReportsPageSpendSummaryProductResult {
    isSpendSummaryVisible: boolean | undefined;
    sku: string | undefined;
    product: SpendProductRowDto | null;
    loading: boolean;
    error: string | null;
    purchases: SpendProductDetailResponseDto | null;
    dateFrom: string;
    dateTo: string;
    datePreset?: string;
    totalSpend: number;
    totalQuantity: number;
    ordersCount: number;
    totalSpendPercent: number;
    lastRequestParamsPresent: boolean;
    canLoadMorePurchases: boolean;
    loadMorePurchases: () => void;
}

export const useReportsPageSpendSummaryProduct =
    (): UseReportsPageSpendSummaryProductResult => {
        const { sku } = useParams<{ sku: string }>();
        const location = useLocation();
        const isSpendSummaryVisible = useSpendSummaryReportVisible();
        const lastRequestParams = useSelector(getReportSpendLastRequestParams);
        const locationState =
            (location.state as SpendDrillInLocationState | null) ?? {};
        const product = locationState.product ?? null;
        const contextTotalSpend = toNumber(locationState.contextTotalSpend);

        const [loading, setLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [purchases, setPurchases] =
            useState<SpendProductDetailResponseDto | null>(null);
        const activeRequestControllerRef = useRef<AbortController | null>(null);

        const loadPurchases = useCallback(
            async (page: number) => {
                if (!sku || !lastRequestParams) {
                    return;
                }

                activeRequestControllerRef.current?.abort();
                const controller = new AbortController();
                activeRequestControllerRef.current = controller;

                setLoading(true);
                setError(null);

                try {
                    const response =
                        await $api.post<SpendProductDetailResponseDto>(
                            `/v1/reports/spend-insights/products/${encodeURIComponent(
                                sku,
                            )}`,
                            buildSpendInsightsBody(
                                buildSpendDrillInRequestParams({
                                    lastRequestParams,
                                    scope: locationState.scope,
                                }),
                                {
                                    orderBy: '-submitted_at',
                                    page,
                                    pageSize: 25,
                                },
                            ),
                            { signal: controller.signal },
                        );

                    setPurchases((prev) => {
                        if (!prev || page === 1) {
                            return response.data;
                        }

                        return {
                            ...response.data,
                            items: [...prev.items, ...response.data.items],
                        };
                    });
                } catch (requestError) {
                    if (axios.isCancel(requestError)) {
                        return;
                    }
                    setError('failed');
                } finally {
                    if (activeRequestControllerRef.current === controller) {
                        activeRequestControllerRef.current = null;
                        setLoading(false);
                    }
                }
            },
            [lastRequestParams, locationState.scope, sku],
        );

        useEffect(() => {
            void loadPurchases(1);

            return () => {
                activeRequestControllerRef.current?.abort();
                activeRequestControllerRef.current = null;
            };
        }, [loadPurchases]);

        const canLoadMorePurchases =
            Boolean(purchases) &&
            purchases!.page < purchases!.totalPages &&
            !loading;

        const loadMorePurchases = useCallback((): void => {
            if (!purchases || purchases.page >= purchases.totalPages) {
                return;
            }
            void loadPurchases(purchases.page + 1);
        }, [loadPurchases, purchases]);

        const { totalSpend, totalQuantity, ordersCount, totalSpendPercent } =
            useMemo(() => {
                const spend = toNumber(product?.totalSpend);
                const quantity = product?.totalQty ?? 0;
                const orders = purchases?.count ?? 0;
                const percent =
                    contextTotalSpend > 0
                        ? (spend / contextTotalSpend) * 100
                        : 0;

                return {
                    totalSpend: spend,
                    totalQuantity: quantity,
                    ordersCount: orders,
                    totalSpendPercent: percent,
                };
            }, [contextTotalSpend, product, purchases?.count]);

        return {
            isSpendSummaryVisible,
            sku,
            product,
            loading,
            error,
            purchases,
            dateFrom: lastRequestParams?.dateFrom ?? '',
            dateTo: lastRequestParams?.dateTo ?? '',
            datePreset: lastRequestParams?.datePreset,
            totalSpend,
            totalQuantity,
            ordersCount,
            totalSpendPercent,
            lastRequestParamsPresent: Boolean(lastRequestParams),
            canLoadMorePurchases,
            loadMorePurchases,
        };
    };
